import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import uuid from 'uuid';
import { Flex, xcss } from '@atlaskit/primitives';
import {
	type ActionMeta,
	type FormatOptionLabelMeta,
	type OptionsType,
	AsyncSelect,
	AsyncCreatableSelect,
} from '@atlaskit/select';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	ReferencedObjectReadView,
	ReferencedObjectCreateView,
} from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/referenced-object/index.tsx';
import type { ObjectAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import type { ObjectProperties } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-properties.tsx';
import type { ReferencedObjectPickerOption as ObjectPickerOption } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/picker-options.tsx';
import { toCmdbObjectId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { useReferencedObjectsSearch } from '../services/index.tsx';
import { messages } from './messages.tsx';
import { mapOptionsFromData, useObjectPickerState } from './utils.tsx';

export type Props = {
	isMultiSelect: boolean;
	validationState: 'default' | 'error';
	attributeType: ObjectAttributeType;
	value: ObjectPickerOption[];
	autoFocus?: boolean;
	menuIsOpen?: boolean;
	/**
	 * This is an array of the 'current' objects attribute values *before* they are saved the
	 * backend (like in the create/clone/bulk-edit forms). This allows for filtering objects based
	 * on attribute values that aren't yet persisted.
	 */
	// eslint-disable-next-line jira/react/handler-naming
	objectProperties: ObjectProperties | (() => ObjectProperties);
	// removedOptions can be passed in when the options can be deselected from outside this component
	removedOptions?: ObjectPickerOption[];
	quickCreateObjects?: boolean;
	onChange: (objectPickerOptions: ObjectPickerOption[]) => void;
	shouldInputRenderValue?: boolean;
	isClearable?: boolean;
	isDisabled?: boolean;
};

type CreatableObjectPickerOption = ObjectPickerOption & { __isNew__?: boolean };

const MAX_HEIGHT_MENU = 400;

const REMOVE_ACTION_META_TYPES: Array<ActionMeta<ObjectPickerOption>['action']> = [
	'clear',
	'remove-value',
	'pop-value',
];

export const ReferencedObjectPicker = ({
	isMultiSelect,
	validationState = 'default',
	attributeType,
	value,
	onChange,
	autoFocus = false,
	menuIsOpen,
	objectProperties,
	quickCreateObjects = false,
	shouldInputRenderValue = true,
	isClearable = true,
	removedOptions = [],
	isDisabled,
}: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();

	const {
		loading,
		data: searchData,
		error: searchError,
		fetch: searchReferencedObjects,
	} = useReferencedObjectsSearch();

	const [defaultOptions, setDefaultOptions] = useObjectPickerState(searchData);
	const selectWrapperRef = useRef<HTMLDivElement>(null);
	const [closeToBottom, setCloseToBottom] = useState<boolean>(false);

	/*  Infer the menu position for asynchronously loaded options. menuPlacement="auto" doesn't work on initial load of
        options and appears below select causing an issue when the select is at the bottom of the window */
	useEffect(() => {
		if (selectWrapperRef.current) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const distance = window.innerHeight - selectWrapperRef.current.getBoundingClientRect().bottom;
			setCloseToBottom(distance < MAX_HEIGHT_MENU);
		}
	}, [menuIsOpen]);

	const loadPickerOptions = useCallback(
		async (query: string, callback?: (arg1: ObjectPickerOption[] | undefined) => void) => {
			// Due to the way forms subscribe to changes we have to explicitly call getState() when we want the values
			// and can't call it in a parent and pass it down as objectProperties
			const objectPropertiesForSearch =
				objectProperties instanceof Function ? objectProperties() : objectProperties;

			const { avatarUUID, hasAvatar, objectTypeId, attributes } = objectPropertiesForSearch;

			const result = await searchReferencedObjects({
				objectProperties: {
					attributes,
					avatarUUID,
					hasAvatar,
					objectTypeId,
				},
				attributeType,
				query,
			});

			const fetchedOptions = mapOptionsFromData(result) ?? [];

			const currentOptions = fg('ignore-removed-options-on-object-reference-attr')
				? []
				: // This allows removed options to also be searched as they are not returned from searchReferencedObjects due to objectProperties
					(defaultOptions ?? []).filter((option) =>
						option.label.toLowerCase().includes(query.toLowerCase()),
					);

			const combinedObjects = fg('ignore-removed-options-on-object-reference-attr')
				? [...currentOptions, ...fetchedOptions, ...removedOptions]
						.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [...currentOptions, ...fetchedOptions, ...removedOptions].sort((a, b) =>
						a.label.localeCompare(b.label),
					);

			callback?.(
				// Remove duplicate objects from list (id should be unique)
				combinedObjects.filter(
					({ id }, index, array) => array.findIndex((object) => object.id === id) === index,
				),
			);
		},
		[objectProperties, searchReferencedObjects, attributeType, defaultOptions, removedOptions],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(
		(query: string, callback?: (arg1: ObjectPickerOption[] | undefined) => void) =>
			loadPickerOptions(query, callback),
		400,
	);

	const handleChange = useCallback(
		(
			values: ObjectPickerOption | OptionsType<ObjectPickerOption> | null,
			meta: ActionMeta<ObjectPickerOption>,
		) => {
			const objects = values ?? [];
			onChange(Array.isArray(objects) ? objects : [objects]);
			// Add removed option(s) back into the defaultOptions
			if (meta?.action && REMOVE_ACTION_META_TYPES.includes(meta.action)) {
				let removedValuesArray: ObjectPickerOption[] = [];
				if (
					(meta.action === 'remove-value' || meta.action === 'pop-value') &&
					meta.removedValue &&
					!Array.isArray(meta.removedValue)
				) {
					removedValuesArray = [meta.removedValue];
				}
				if (meta.action === 'clear' && meta.removedValues && Array.isArray(meta.removedValues)) {
					removedValuesArray = meta.removedValues;
				}
				// Ignore newly created objects
				removedValuesArray = removedValuesArray.filter(
					(removeValue) => removeValue?.objectKey && removeValue.objectKey !== '',
				);
				if (removedValuesArray.length) {
					setDefaultOptions((prevState) => {
						if (prevState) {
							// Remove duplicate objects from list (id should be unique) and sort by label
							return [...prevState, ...removedValuesArray]
								.filter(
									({ id }, index, array) => array.findIndex((object) => object.id === id) === index,
								)
								.sort((a, b) => a.label.localeCompare(b.label));
						}
						return [];
					});
				}
			}
		},
		[onChange, setDefaultOptions],
	);

	const handleCreateOption = useCallback(
		(inputValue: string) => {
			const options = defaultOptions ?? [];
			const inMemoryId = uuid.v4();
			const newOption = {
				id: toCmdbObjectId(inMemoryId),
				label: inputValue,
				value: `CREATE:${inputValue}`,
				icon: attributeType.referenceObjectType.icon,
				objectKey: '',
			};
			setDefaultOptions([...options, newOption].sort((a, b) => a.label.localeCompare(b.label)));
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			handleChange(isMultiSelect ? [...value, newOption] : newOption, {
				action: 'create-option',
			} as ActionMeta<ObjectPickerOption>);
		},
		[defaultOptions, setDefaultOptions, attributeType, handleChange, isMultiSelect, value],
	);

	useEffect(() => {
		if (searchError) {
			showFlag({
				type: 'error',
				title: messages.errorFlagTitle,
				description: messages.errorFlagDescription,
				isAutoDismiss: true,
			});
		}
	}, [searchError, showFlag]);

	const AsyncSelectComponent = useMemo(
		() => (quickCreateObjects ? AsyncCreatableSelect : AsyncSelect),
		[quickCreateObjects],
	);

	const formatOptionLabel = useCallback(
		(
			{ __isNew__ = false, ...option }: CreatableObjectPickerOption,
			meta: FormatOptionLabelMeta<ObjectPickerOption>,
		) => {
			if (__isNew__) {
				return (
					<ReferencedObjectCreateView
						label={`${option.value} (${formatMessage(messages.newObjectLabel)})`}
						icon={attributeType.referenceObjectType.icon}
					/>
				);
			}
			return option.icon ? (
				<ReferencedObjectCreateView label={option.label} icon={option.icon} />
			) : (
				<ReferencedObjectReadView
					id={option.id}
					objectKey={option.value}
					label={option.label}
					avatar={option.avatar}
					appearance="plain"
					includeLink={false}
					// Don't add max-width for options shown in the dropdown.
					// This allows the dropdown to truncate available options
					// based on its own dynamic width.
					forceMaxWidth={isMultiSelect && meta.context !== 'menu'}
				/>
			);
		},
		[isMultiSelect, formatMessage, attributeType.referenceObjectType.icon],
	);

	return (
		<div ref={selectWrapperRef}>
			<AsyncSelectComponent
				{...selectInAModalStyleFixProps}
				isDisabled={isDisabled}
				controlShouldRenderValue={shouldInputRenderValue}
				isClearable={isClearable}
				autoFocus={autoFocus}
				menuIsOpen={menuIsOpen}
				menuPlacement={closeToBottom ? 'top' : 'auto'}
				defaultOptions={defaultOptions}
				hideSelectedOptions
				value={value}
				isLoading={loading}
				isMulti={isMultiSelect}
				loadOptions={debouncedLoadOptions}
				formatOptionLabel={formatOptionLabel}
				validationState={validationState}
				onChange={handleChange}
				onCreateOption={handleCreateOption}
				// Need to always fetch incase objectProperties (other fields for the qlQuery) has changed
				onFocus={() => loadPickerOptions('', setDefaultOptions)}
				components={{ MultiValueContainer }}
				classNamePrefix="insight-object-attribute-referenced-object-picker"
			/>
		</div>
	);
};

const MultiValueContainer = ({ children }: { children?: ReactNode }) => (
	<Flex alignItems="center" xcss={multiValueContainerStyles}>
		{children}
	</Flex>
);

const multiValueContainerStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: 'border.radius',
	marginRight: 'space.050',
	maxWidth: '100%',
});
